import type { ElementKey } from '@/lib/experience'
import { ELEMENTS, ELEMENT_TRINDADE, splitTrindade } from '@/lib/experience'
import { motion } from 'framer-motion'

interface Props {
  element: ElementKey
  situationIndex: number
  onContinue: () => void
}

interface MovementProps {
  index: number
  label: string
  glyph: string
  text: string
  accent: string
  accentSoft: string
  delay: number
}

function Movement({ index, label, glyph, text, accent, accentSoft, delay }: MovementProps) {
  if (!text) return null
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      <div className="flex items-center gap-3 sm:gap-4 mb-5">
        <span
          className="font-display text-[30px] sm:text-[36px] md:text-[44px] leading-none flex-shrink-0"
          style={{ color: accentSoft }}
          aria-hidden
        >
          {glyph}
        </span>
        <div className="flex-1 flex items-center gap-2 sm:gap-3 min-w-0">
          <span
            className="font-display italic numerals text-[13px] sm:text-[14px] tracking-[0.1em] flex-shrink-0"
            style={{ color: accentSoft }}
          >
            {String(index).padStart(2, '0')}
          </span>
          <span
            className="text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] uppercase font-body flex-shrink-0"
            style={{ color: accent }}
          >
            {label}
          </span>
          <span
            className="flex-1 h-px min-w-[12px]"
            style={{
              background: `linear-gradient(90deg, ${accentSoft}55, transparent)`,
            }}
          />
        </div>
      </div>

      <p className="font-body type-trindade text-[var(--ink)] pl-0 md:pl-14">
        {text}
      </p>
    </motion.div>
  )
}

function StepTrindade({ element, situationIndex, onContinue }: Props) {
  const meta = ELEMENTS[element]
  const raw = ELEMENT_TRINDADE[element]?.[situationIndex] ?? ''
  const parts = splitTrindade(raw)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-3xl mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-12 md:mb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
          style={{ color: meta.accentSoft }}
        >
          Elemento {meta.label} · Etapa <span className="numerals">5</span>
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="font-display type-hero text-[var(--ink)] mb-6"
        >
          Sua <em className="italic font-light" style={{ color: meta.accentSoft }}>Trindade</em> de Cura
        </motion.h1>

        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 120, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="h-px mx-auto mb-7"
          style={{
            background: `linear-gradient(90deg, transparent, ${meta.accentSoft}, transparent)`,
          }}
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="font-body type-body text-[var(--ink-soft)] max-w-xl mx-auto mb-5"
        >
          Leia diariamente para liberar esse bloqueio. Ao final dessa página você tem acesso a uma meditação para remover os bloqueios do elemento <span className="italic" style={{ color: meta.accentSoft }}>{meta.label}</span>. Escuta agora para já iniciar essa liberação. Lembrando que tudo isso também chega no seu e-mail, para você guardar e repetir sempre que precisar.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="font-body italic type-body text-[var(--ink-soft)] max-w-lg mx-auto"
        >
          Leia devagar. Coloque a mão no coração. Permita que cada frase faça eco dentro de você.
        </motion.p>
      </div>

      {/* Trindade body */}
      {parts ? (
        <div className="card-elevated rounded-sm p-5 sm:p-6 md:p-12 space-y-9 sm:space-y-10 md:space-y-12 relative overflow-hidden">
          {/* Glow behind */}
          <div
            className="absolute inset-x-0 -top-40 h-80 pointer-events-none opacity-60"
            style={{
              background: `radial-gradient(closest-side, ${meta.accentSoft}22, transparent)`,
            }}
          />

          <Movement
            index={1}
            label="Reconheço"
            glyph="❖"
            text={parts.reconheco}
            accent={meta.accent}
            accentSoft={meta.accentSoft}
            delay={0.7}
          />

          <div
            className="h-px mx-auto"
            style={{
              maxWidth: 200,
              background: `linear-gradient(90deg, transparent, ${meta.accentSoft}44, transparent)`,
            }}
          />

          <Movement
            index={2}
            label="Agradeço"
            glyph="❋"
            text={parts.agradeco}
            accent={meta.accent}
            accentSoft={meta.accentSoft}
            delay={1.1}
          />

          <div
            className="h-px mx-auto"
            style={{
              maxWidth: 200,
              background: `linear-gradient(90deg, transparent, ${meta.accentSoft}44, transparent)`,
            }}
          />

          <Movement
            index={3}
            label="Eu Sou"
            glyph="✧"
            text={parts.euSou}
            accent={meta.accent}
            accentSoft={meta.accentSoft}
            delay={1.5}
          />
        </div>
      ) : (
        <div className="card-elevated rounded-sm p-8 text-center">
          <p className="font-body italic text-[var(--ink-soft)] text-[15px] leading-relaxed">
            A Trindade de Cura para este caminho está sendo preparada para você.
          </p>
        </div>
      )}

      {/* Closing */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 2.0 }}
        className="mt-12 md:mt-16 text-center"
      >
        <p
          className="font-display italic type-section mb-10 max-w-xl mx-auto"
          style={{ color: meta.accentSoft }}
        >
          Respire. O campo já está começando a se reorganizar.
        </p>

        <button
          onClick={onContinue}
          className="btn-primary w-full sm:w-auto px-6 sm:px-10 md:px-12 py-4 min-h-[52px] rounded-sm type-button tracking-[0.12em] sm:tracking-[0.15em] uppercase"
        >
          Receber minha meditação
        </button>
      </motion.div>
    </motion.div>
  )
}

export default StepTrindade
